
import { User } from '../types';
import { supabase } from '../supabase';
import { KEYS } from './core';

// Convert Supabase User to our app User type
const convertSupabaseUser = (supabaseUser: any): User | null => {
  if (!supabaseUser) return null;
  
  return {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    name: supabaseUser.user_metadata?.name || '',
    createdAt: new Date(supabaseUser.created_at),
  };
};

// Get user from Supabase auth session with fallback to localStorage for backward compatibility
export const getUser = async (): Promise<User | null> => {
  // Try to get user from Supabase
  const { data: { session }, error } = await supabase.auth.getSession();
  
  if (session?.user) {
    return convertSupabaseUser(session.user);
  }
  
  // Fallback to localStorage for backward compatibility
  const localData = localStorage.getItem(KEYS.USER);
  return localData ? JSON.parse(localData) : null;
};

// Set user in Supabase (for backward compatibility, we'll also store in localStorage)
export const setUser = (user: User | null): void => {
  // For legacy systems, we'll still update localStorage
  if (user) {
    localStorage.setItem(KEYS.USER, JSON.stringify(user));
  } else {
    clearUser();
  }
};

// Clear user data and sign out from Supabase
export const clearUser = async (): Promise<void> => {
  await supabase.auth.signOut();
  localStorage.removeItem(KEYS.USER);
};

// Sign up a new user
export const signUpUser = async (
  email: string, 
  password: string, 
  name: string
): Promise<{ user: User | null; error: any }> => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        name,
      },
    },
  });
  
  let user = null;
  if (data.user) {
    user = convertSupabaseUser(data.user);
    setUser(user); // For backward compatibility
  }
  
  return { user, error };
};

// Sign in an existing user
export const signInUser = async (
  email: string, 
  password: string
): Promise<{ user: User | null; error: any }> => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  
  let user = null;
  if (data.user) {
    user = convertSupabaseUser(data.user);
    setUser(user); // For backward compatibility
  }
  
  return { user, error };
};

// Send a password reset email
export const resetPassword = async (email: string): Promise<{ error: any }> => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  
  return { error };
};

// Update user password (used after reset link is clicked)
export const updatePassword = async (
  newPassword: string
): Promise<{ error: any }> => {
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });
  
  return { error };
};

// Update user profile data
export const updateUserProfile = async (
  userData: { name?: string; email?: string }
): Promise<{ user: User | null; error: any }> => {
  const { data, error } = await supabase.auth.updateUser({
    email: userData.email,
    data: { name: userData.name },
  });
  
  let user = null;
  if (data.user) {
    user = convertSupabaseUser(data.user);
    setUser(user); // For backward compatibility
  }
  
  return { user, error };
};

// Delete user account
export const deleteUserAccount = async (): Promise<{ error: any }> => {
  try {
    // Get the current session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { error: new Error('No active session found') };
    }
    
    // Call the edge function to delete the user
    const { error } = await supabase.functions.invoke('delete-user', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });
    
    if (error) {
      return { error };
    }
    
    // Clear local user data
    await clearUser();
    
    return { error: null };
  } catch (error) {
    return { error };
  }
};
